import { visit } from "unist-util-visit"

// Builds the paragraph visitor that swaps the first letter of the first paragraph
// for a decorative drop cap. Exported so it can be unit-tested directly, without
// the unist-util-visit traversal (which is mocked out in the jest setup).
export function dropcapVisitor(options = {}) {
  const dropcapClass =
    options && "dropcapClass" in options ? options.dropcapClass : "dropcap"
  const invisibleClass =
    options && "invisibleClass" in options ? options.invisibleClass : "sr-only"

  let count = 0

  return function (node, index) {
    // only work on first valid markdown paragraph encountered
    if (
      !options.renderDropcap ||
      count > 0 ||
      index > 1 ||
      !("children" in node) ||
      node.children.length <= 0
    ) {
      return
    }

    // The first paragraph often opens with an inline node rather than raw text — e.g. a bold
    // **song name** (`strong`) or a link. Descend through the leading child until we reach the first
    // text node, and cap *that*, injecting the decorative spans into its immediate container so the cap
    // sits at the very start of the visible text.
    let container = node
    let firstText = node.children[0]
    while (
      firstText &&
      !("value" in firstText) &&
      "children" in firstText &&
      firstText.children &&
      firstText.children.length > 0
    ) {
      container = firstText
      firstText = firstText.children[0]
    }

    if (!firstText || !("value" in firstText) || typeof firstText.value !== "string") {
      return
    }

    const para = firstText.value
    const word = para.split(" ")[0]
    const letter = word.charAt(0)
    const part = word.substr(1)
    const text = para.substr(word.length)

    // The decorative cap only supports A–Z. If the first letter is anything else
    // (a fada like Á/É, a number, punctuation), leave the paragraph untouched
    // rather than render a broken cap — relevant for Irish, which is fada-heavy.
    if (!/^[a-z]$/i.test(letter)) {
      return
    }

    count++

    // Keep the *rest* of the first word plus the rest of the paragraph as normal,
    // visible text ("eap rest…"). Only the single first letter is swapped for the
    // decorative cap.
    //
    // SEO fix: previously the whole first word was ALSO duplicated into an sr-only
    // span while the aria-hidden branch rendered the remaining letters — but neither
    // aria-hidden nor sr-only hides text from search-engine snippet extraction, so
    // crawlers read the word twice as mangled "eapCeap"/"tineTine". Now nothing is
    // duplicated: one decorative letter (no text) + one sr-only letter + visible rest.
    firstText.value = part + text

    container.children.unshift(
      // Decorative illuminated first letter, rendered as an SVG by CelticDropCap
      // (aria-hidden, empty alt) so it contributes no text to AT or search snippets.
      {
        type: "emphasis",
        children: [
          {
            type: "text",
            value: letter,
          },
        ],
        data: {
          hName: "span",
          hProperties: { ariaHidden: "true", className: dropcapClass },
        },
      },
      // The first letter as visually-hidden real text, so the word reads correctly
      // as letter + part ("C" + "eap" = "Ceap") for screen readers and crawlers —
      // exactly once.
      {
        type: "emphasis",
        children: [
          {
            type: "text",
            value: letter,
          },
        ],
        data: {
          hName: "span",
          hProperties: { className: invisibleClass },
        },
      }
    )
  }
}

export default function RemarkDropcap(options = {}) {
  const visitor = dropcapVisitor(options)

  return function transformer(node, file) {
    visit(node, "paragraph", visitor)
  }
}
